import type { ReactNode } from 'react';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { useTranslation } from 'next-i18next';
import type { ClassGroupRes as ClassroomRes } from '@/api/gen/_model/classGroupRes';
import { ArrowTurnUprightOutlinedIcon, LinkDisconnectIcon, PersonStackIcon, PlusIcon } from '@/icons';
import { TextAvatar } from '@/shared/components';
import { confirmDialog, Tooltip, TooltipContent } from '@/ui_v3';
import { cn } from '@/utils/tailwind.util';
function ClassroomLinkTooltipContent({
  icon,
  label
}: {
  icon: ReactNode;
  label: string;
}) {
  return <TooltipContent side="top" align="center" sideOffset={4} className="z-[9999] flex items-center gap-3xs rounded-xs bg-opacity-black-9 p-2xs text-body-sm-semibold text-inverse shadow-lg max-mobile-lg:hidden" data-sentry-element="TooltipContent" data-sentry-component="ClassroomLinkTooltipContent" data-sentry-source-file="ClassroomLinkCard.tsx">
      {icon}
      <span className="whitespace-nowrap">{label}</span>
    </TooltipContent>;
}
function LinkActionBadge({
  linked
}: {
  linked: boolean;
}) {
  return <span className={cn('absolute left-[calc(50%+20.5px)] top-[calc(50%-7.5px)] flex size-[28px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-[3px] border-inverse', linked && 'hidden group-hover:flex max-mobile-lg:flex')} data-sentry-component="LinkActionBadge" data-sentry-source-file="ClassroomLinkCard.tsx">
      <span className="flex size-[22px] items-center justify-center rounded-full border border-opacity bg-default p-0">
        {linked ? <LinkDisconnectIcon width={12} height={12} fill={ICON_COLOR.neutral} /> : <PlusIcon width={12} height={12} fill={ICON_COLOR.neutral} />}
      </span>
    </span>;
}
export function ClassroomLinkCard({
  classroom,
  isPending,
  isLinked,
  linkedClassLinkId,
  className,
  onLink,
  onUnlink
}: {
  classroom: ClassroomRes;
  isPending: boolean;
  isLinked: boolean;
  linkedClassLinkId?: string;
  className?: string;
  onLink: (classroomId: string) => void;
  onUnlink: (classroomId: string, classLinkId: string) => void;
}) {
  const {
    t
  } = useTranslation(['common']);
  const tooltipLabel = isLinked ? t('common:classroomLink.unlinkTooltip') : t('common:classroomLink.linkTooltip');
  const tooltipIcon = isLinked ? <LinkDisconnectIcon size="xs" fill={ICON_COLOR.inverse} /> : <ArrowTurnUprightOutlinedIcon size="xs" fill={ICON_COLOR.inverse} />;
  const handleClick = async () => {
    if (isPending) return;
    if (isLinked && linkedClassLinkId) {
      const confirmed = await confirmDialog.openAsync({
        size: 'xs',
        header: {
          title: t('common:classroomLink.unlinkConfirmTitle')
        },
        body: t('common:classroomLink.unlinkConfirmBody'),
        cancelText: t('common:classroomLink.unlinkCancelButton'),
        confirmText: t('common:classroomLink.unlinkConfirmButton'),
        confirmIntent: 'destructive',
        shouldCloseOnDimClick: false
      });
      if (!confirmed) return;
      onUnlink(classroom.classGroupId, linkedClassLinkId);
      return;
    }
    onLink(classroom.classGroupId);
  };
  return <Tooltip.Root data-sentry-element="unknown" data-sentry-component="ClassroomLinkCard" data-sentry-source-file="ClassroomLinkCard.tsx">
      <Tooltip.Trigger asChild data-sentry-element="unknown" data-sentry-source-file="ClassroomLinkCard.tsx">
        <button type="button" className={cn('group relative z-10 flex h-[117px] w-[97px] shrink-0 flex-col items-center gap-sm rounded-md px-2xs py-xs transition-colors hover:z-50 hover:bg-gray-50 disabled:cursor-wait', 'max-mobile-lg:h-[115px] max-mobile-lg:w-[90px] max-mobile-lg:min-w-[90px]', className)} disabled={isPending} onClick={() => void handleClick()}>
          <TextAvatar text={classroom.name} colorSeed={classroom.classGroupId} size="md" className={cn('size-[48px] text-body-lg-bold', !isLinked && 'opacity-30')} data-sentry-element="TextAvatar" data-sentry-source-file="ClassroomLinkCard.tsx" />
          <span className="flex w-full min-w-0 flex-col items-center gap-3xs">
            <span className="w-full truncate text-center text-body-sm-semibold text-neutral">
              {classroom.name}
            </span>
            <span className="inline-flex h-[18px] min-w-[18px] items-center justify-center gap-4xs rounded-full border border-alternative px-3xs">
              <PersonStackIcon width={10} height={10} fill={ICON_COLOR.neutral} data-sentry-element="PersonStackIcon" data-sentry-source-file="ClassroomLinkCard.tsx" />
              <span className="text-caption-md-semibold text-neutral">
                {classroom.memberCount}
              </span>
            </span>
          </span>
          <LinkActionBadge linked={isLinked} data-sentry-element="LinkActionBadge" data-sentry-source-file="ClassroomLinkCard.tsx" />
        </button>
      </Tooltip.Trigger>
      <ClassroomLinkTooltipContent icon={tooltipIcon} label={tooltipLabel} data-sentry-element="ClassroomLinkTooltipContent" data-sentry-source-file="ClassroomLinkCard.tsx" />
    </Tooltip.Root>;
}